import {toCapitalizeCase, toPascalCase} from '@/util/misc';
import {z} from 'zod';

const constraintSchema = z.object({
  type: z.enum(['min', 'max']),
  value: z.union([z.number(), z.string().date()]),
});

// Zod Schema for a single column
const columnSchema = z.object({
  id: z.string().optional(),
  columnName: z.preprocess(
    val => (typeof val === 'string' ? val.trim() : val),
    z.string().min(1, 'Column name cannot be empty.'),
  ),
  oldColumnName: z.string().optional(),
  // .transform(toSnakeCase),
  dataType: z.string().min(1, 'Data type is required.'),
  isNullable: z.boolean().default(false),
  mapping: z.string(),
  realColumnNameFromMapping: z.string().optional(),
  constraints: z
    .array(constraintSchema)
    .optional()
    .superRefine((constraints, ctx) => {
      // Avoid duplicated validators
      if (!constraints) return;
      const types = new Set();
      for (let i = 0; i < constraints.length; i++) {
        const {type} = constraints[i];
        if (types.has(type)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Duplicate constraint type: "${type}"`,
            path: [i],
          });
        }
        types.add(type);
      }

      // Avoid min and max incherence
      const min = constraints.find(c => c.type === 'min')?.value;
      const max = constraints.find(c => c.type === 'max')?.value;
      if (min !== undefined && max !== undefined && min > max) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Minimum value (${min}) cannot be greater than maximum value (${max}).`,
        });
      }
    }),
});

export const columnInputSchema = columnSchema.omit({id: true});

export const requiredFicMappings = [
  'policy_number',
  'commission_date',
  'commission_amount',
];

const createTableBuilderSchema = (requiredMappings: string[]) => {
  return z.object({
    oldTableName: z.string().optional(),
    carrierId: z.string().optional(),
    overrideAndCommission: z.string().optional(),
    tableName: z
      .string()
      .min(1, 'Table name cannot be empty.')
      .transform(toPascalCase),
    columns: z.array(columnSchema).superRefine((columns, ctx) => {
      const seen = new Set<string>();
      for (let i = 0; i < columns.length; i++) {
        const name = columns[i].columnName;
        if (seen.has(name)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Duplicate column name: "${name}"`,
            path: [i, 'columnName'],
          });
        } else {
          seen.add(name);
        }
      }

      const presentMappings = new Set(columns.map(col => col.mapping));
      for (const required of requiredMappings) {
        if (!presentMappings.has(required)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Missing required mapping for "${toCapitalizeCase(required)}".`,
            path: ['columns'],
          });
        }
      }
    }),
  });
};

export const carrierTableBuilderSchema = createTableBuilderSchema([
  'policy_number',
  'commission_date',
  'commission_amount',
  'agent_matcher',
]);

export const carrierOverrideTableBuilderSchema = createTableBuilderSchema([
  'policy_number',
  'commission_date',
  'override',
  'lives',
  'agent_matcher',
]);

export const mtTableBuilderSchema = createTableBuilderSchema([
  'policy_number',
  'original_effective_date',
  'agent_matcher',
]);

export type CarrierTableBuilder = z.infer<typeof carrierTableBuilderSchema>;
export type CarrierOverrideTableBuilder = z.infer<
  typeof carrierOverrideTableBuilderSchema
>;
export type MtTableBuilder = z.infer<typeof mtTableBuilderSchema>;

export type Column = z.infer<typeof columnInputSchema>;
export type Constraint = z.infer<typeof constraintSchema>;
